import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Modal } from '../components/UI';
import { formatDate } from '../utils/helpers';

export default function TripInfo() {
  const { contacts, notes, tickets, addContact, deleteContact, addNote, deleteNote, addTicket, deleteTicket, showToast, getFileUrl } = useApp();
  const [modal, setModal] = useState(null); // 'contact' | 'note' | 'ticket'
  const [uploading, setUploading] = useState(false);

  const handleAddContact = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const name = fd.get('name').trim(), phone = fd.get('phone').trim();
    if (!name || !phone) return showToast('Fill all fields', 'error');
    await addContact({ name, phone });
    showToast('Contact added', 'success');
    setModal(null);
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    const content = e.target.content.value.trim();
    if (!content) return showToast('Note cannot be empty', 'error');
    await addNote({ content });
    showToast('Note added', 'success');
    setModal(null);
  };

  const handleUploadTicket = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const name = fd.get('name').trim();
    const file = fd.get('file');
    if (!name || !file?.size) return showToast('Provide name and file', 'error');
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        await addTicket({ name, fileName: file.name, fileData: ev.target.result });
        showToast('Ticket uploaded', 'success');
        setUploading(false);
        setModal(null);
      };
      reader.readAsDataURL(file);
    } catch {
      showToast('Upload failed', 'error');
      setUploading(false);
    }
  };

  const confirmDelete = (label, deleteFn) => {
    if (window.confirm(`Delete this ${label}?`)) deleteFn();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">Trip Info</h2>
          <div className="page-subtitle">Contacts, Notes & Tickets</div>
        </div>
      </div>

      <div className="info-grid">
        {/* Contacts */}
        <div className="info-section">
          <div className="section-header">
            <h3 className="section-title">📞 Contacts</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('contact')}>+ Add</button>
          </div>
          {contacts.length === 0
            ? <div className="empty-state-sm">No contacts added yet.</div>
            : contacts.map(c => (
              <div className="info-card" key={c.id}>
                <div className="info-main">
                  <div className="info-title">{c.name}</div>
                  <div className="info-sub"><a href={`tel:${c.phone}`}>{c.phone}</a></div>
                </div>
                <button className="btn-icon text-red" onClick={() => confirmDelete('contact', () => { deleteContact(c.id); showToast('Contact deleted', 'info'); })}>🗑️</button>
              </div>
            ))
          }
        </div>

        {/* Notes */}
        <div className="info-section">
          <div className="section-header">
            <h3 className="section-title">📝 Notes</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('note')}>+ Add</button>
          </div>
          {notes.length === 0
            ? <div className="empty-state-sm">No notes added yet.</div>
            : notes.map(n => (
              <div className="info-card note-card" key={n.id}>
                <div className="info-main">
                  <div className="note-content" dangerouslySetInnerHTML={{ __html: n.content.replace(/\n/g, '<br>') }} />
                  <div className="note-date">{formatDate(n.date)}</div>
                </div>
                <button className="btn-icon text-red" onClick={() => confirmDelete('note', () => { deleteNote(n.id); showToast('Note deleted', 'info'); })}>🗑️</button>
              </div>
            ))
          }
        </div>

        {/* Tickets */}
        <div className="info-section">
          <div className="section-header">
            <h3 className="section-title">🎟️ Tickets</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('ticket')}>+ Upload</button>
          </div>
          {tickets.length === 0
            ? <div className="empty-state-sm">No tickets uploaded yet.</div>
            : tickets.map(t => {
              const isImg = /\.(jpeg|jpg|gif|png)$/i.test(t.filePath);
              const fileUrl = getFileUrl(t.filePath);
              return (
                <div className="info-card ticket-card" key={t.id}>
                  {isImg
                    ? <img src={fileUrl} className="ticket-thumb" alt={t.name} />
                    : <div className="ticket-thumb-doc">📄</div>
                  }
                  <div className="info-main">
                    <div className="info-title">{t.name}</div>
                    <div className="info-sub"><a href={fileUrl} target="_blank" rel="noreferrer">View File</a></div>
                  </div>
                  <button className="btn-icon text-red" onClick={() => confirmDelete('ticket', () => { deleteTicket(t.id); showToast('Ticket deleted', 'info'); })}>🗑️</button>
                </div>
              );
            })
          }
        </div>
      </div>

      {/* Contact Modal */}
      <Modal isOpen={modal === 'contact'} onClose={() => setModal(null)} title="Add Contact">
        <form onSubmit={handleAddContact}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input name="name" type="text" className="form-input" placeholder="e.g. Hotel Reception" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input name="phone" type="text" className="form-input" placeholder="e.g. +91 9876543210" />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>
      </Modal>

      {/* Note Modal */}
      <Modal isOpen={modal === 'note'} onClose={() => setModal(null)} title="Add Note">
        <form onSubmit={handleAddNote}>
          <div className="form-group">
            <label className="form-label">Note Content</label>
            <textarea name="content" className="form-input form-textarea" placeholder="Type your note here..." autoFocus />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>
      </Modal>

      {/* Ticket Modal */}
      <Modal isOpen={modal === 'ticket'} onClose={() => setModal(null)} title="Upload Ticket">
        <form onSubmit={handleUploadTicket}>
          <div className="form-group">
            <label className="form-label">Ticket Name</label>
            <input name="name" type="text" className="form-input" placeholder="e.g. Train to Shimla" autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">File (Image or PDF)</label>
            <input name="file" type="file" className="form-input" accept="image/*,.pdf" />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
